// Normalizador e formatadores do Relatório do fechamento v2.
// A função devolve jsonb; aqui só lemos o que existe e garantimos números seguros.
import type {
  AcaoItem,
  DespesasResumo,
  EntradasAvulsas,
  InscricoesPublico,
  InscricoesResumo,
  LojaRel,
  OndeEsta,
  Principal,
  RelatorioV2,
  TemSecoes,
  TopVendedor,
} from '@/pages/gerenciar/painel/financeiro/relatorio/types';

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const bool = (v: unknown): boolean => v === true;

const txt = (v: unknown, padrao = ''): string => (v == null ? padrao : String(v));

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

const lista = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : [];

// --- Loja (formato herdado de relatorio_fechamento) ---

function normalizarLoja(v: unknown): LojaRel | null {
  if (!v || typeof v !== 'object') return null;
  const o = obj(v);
  const t = obj(o.totais);
  return {
    evento_id: num(o.evento_id),
    gerado_em: txt(o.gerado_em),
    totais: {
      pedidos: num(t.pedidos),
      vendido_centavos: num(t.vendido_centavos),
      taxa_plataforma_centavos: num(t.taxa_plataforma_centavos),
      taxa_gateway_centavos: num(t.taxa_gateway_centavos),
      liquido_centavos: num(t.liquido_centavos),
    },
    por_metodo: lista(o.por_metodo).map((m) => ({
      origem: txt(m.origem, 'online'),
      metodo: txt(m.metodo, '-'),
      pedidos: num(m.pedidos),
      total_centavos: num(m.total_centavos),
    })),
    por_caixa: lista(o.por_caixa).map((c) => {
      const r = c.resumo ? obj(c.resumo) : null;
      return {
        caixa: c.caixa == null ? null : String(c.caixa),
        operador: c.operador == null ? null : String(c.operador),
        aberto_em: c.aberto_em == null ? null : String(c.aberto_em),
        fechado_em: c.fechado_em == null ? null : String(c.fechado_em),
        troco_inicial_centavos: num(c.troco_inicial_centavos),
        esperado_centavos: num(c.esperado_centavos),
        contado_centavos: c.contado_centavos == null ? null : num(c.contado_centavos),
        diferenca_centavos: c.diferenca_centavos == null ? null : num(c.diferenca_centavos),
        sangria_centavos: num(c.sangria_centavos),
        dinheiro_centavos: num(c.dinheiro_centavos),
        resumo: r
          ? {
              dinheiro_vendas: num(r.dinheiro_vendas),
              dinheiro_centavos: num(r.dinheiro_centavos),
              pix_centavos: num(r.pix_centavos),
              cartao_centavos: num(r.cartao_centavos),
              desfeitas: num(r.desfeitas),
            }
          : null,
        sangrias: lista(c.sangrias).map((s) => ({
          valor_centavos: num(s.valor_centavos),
          observacao: s.observacao == null ? null : String(s.observacao),
          em: txt(s.em),
        })),
      };
    }),
    por_barraca: lista(o.por_barraca).map((b) => ({
      ponto_id: b.ponto_id == null ? null : num(b.ponto_id),
      barraca: txt(b.barraca, 'Sem barraca'),
      vendido_qtd: num(b.vendido_qtd),
      vendido_centavos: num(b.vendido_centavos),
      retirado_qtd: num(b.retirado_qtd),
      a_retirar_qtd: num(b.a_retirar_qtd),
      produtos: lista(b.produtos).map((p) => ({
        produto: txt(p.produto, 'Item'),
        qtd: num(p.qtd),
        valor_centavos: num(p.valor_centavos),
        retirado: num(p.retirado),
      })),
    })),
    sangrias: num(o.sangrias),
  };
}

// --- Seções ---

function normalizarPrincipal(v: unknown): Principal {
  const o = obj(v);
  return {
    entrou_centavos: num(o.entrou_centavos),
    taxas_centavos: num(o.taxas_centavos),
    despesas_centavos: num(o.despesas_centavos),
    resultado_centavos: num(o.resultado_centavos),
  };
}

function normalizarInscricoesPorPublico(v: unknown): InscricoesPublico[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return v.map((item) => {
    const p = obj(item);
    return {
      publico: txt(p.publico, 'Inscritos'),
      papel: p.papel == null ? null : String(p.papel),
      total: num(p.total),
      suplentes: num(p.suplentes),
      quitadas: num(p.quitadas),
      pago_centavos: num(p.pago_centavos),
      em_aberto_centavos: num(p.em_aberto_centavos),
    };
  });
}

function normalizarInscricoes(v: unknown): InscricoesResumo | null {
  if (!v || typeof v !== 'object') return null;
  const o = obj(v);
  return {
    total: num(o.total),
    canceladas: num(o.canceladas),
    isentas: num(o.isentas),
    devido_centavos: num(o.devido_centavos),
    pago_centavos: num(o.pago_centavos),
    pago_pela_acao_centavos: num(o.pago_pela_acao_centavos),
    pago_direto_centavos: num(o.pago_direto_centavos),
    em_aberto_centavos: num(o.em_aberto_centavos),
    quitadas: num(o.quitadas),
    parciais: num(o.parciais),
    sem_pagamento: num(o.sem_pagamento),
    suplentes: o.suplentes == null ? undefined : num(o.suplentes),
    por_publico: normalizarInscricoesPorPublico(o.por_publico),
  };
}

function normalizarTop(v: unknown): TopVendedor[] {
  return lista(v).map((t) => ({ nome: txt(t.nome, 'Sem nome'), numeros: num(t.numeros) }));
}

function normalizarAcao(v: unknown): AcaoItem[] {
  return lista(v).map((a) => ({
    rifa_id: num(a.rifa_id),
    premio: txt(a.premio),
    sorteio_em: a.sorteio_em == null ? null : String(a.sorteio_em),
    valor_numero_centavos: num(a.valor_numero_centavos),
    numeros_total: num(a.numeros_total),
    numeros_vendidos: num(a.numeros_vendidos),
    vendido_centavos: num(a.vendido_centavos),
    potencial_centavos: num(a.potencial_centavos),
    vendedores_total: num(a.vendedores_total),
    vendedores_com_venda: num(a.vendedores_com_venda),
    abatido_em_inscricoes_centavos: num(a.abatido_em_inscricoes_centavos),
    top_vendedores: normalizarTop(a.top_vendedores),
  }));
}

function normalizarDespesas(v: unknown): DespesasResumo {
  const o = obj(v);
  return {
    total_centavos: num(o.total_centavos),
    quantidade: num(o.quantidade),
    sem_comprovante: num(o.sem_comprovante),
    por_categoria: lista(o.por_categoria).map((c) => ({
      categoria: txt(c.categoria, 'Outros'),
      total_centavos: num(c.total_centavos),
      quantidade: num(c.quantidade),
    })),
  };
}

function normalizarEntradas(v: unknown): EntradasAvulsas | null {
  if (!v || typeof v !== 'object') return null;
  const o = obj(v);
  return { total_centavos: num(o.total_centavos), quantidade: num(o.quantidade) };
}

function normalizarOndeEsta(v: unknown): OndeEsta | null {
  if (!v || typeof v !== 'object') return null;
  const o = obj(v);
  return {
    na_conta_mp: num(o.na_conta_mp),
    direto: num(o.direto),
    com_seliga: num(o.com_seliga),
    sem_destino: num(o.sem_destino),
    repassado: num(o.repassado),
    recebido_do_organizador: num(o.recebido_do_organizador),
    seliga_devida: num(o.seliga_devida),
    saldo: num(o.saldo),
    situacao: txt(o.situacao),
  };
}

function normalizarTem(v: unknown): TemSecoes {
  const o = obj(v);
  return {
    inscricoes: bool(o.inscricoes),
    acao_entre_amigos: bool(o.acao_entre_amigos),
    loja: bool(o.loja),
    despesas: bool(o.despesas),
    entradas_avulsas: bool(o.entradas_avulsas),
  };
}

export function normalizarRelatorio(v: unknown): RelatorioV2 | null {
  if (!v || typeof v !== 'object') return null;
  const o = obj(v);
  return {
    evento_id: num(o.evento_id),
    gerado_em: txt(o.gerado_em),
    perfil: txt(o.perfil),
    frase: txt(o.frase),
    tem: normalizarTem(o.tem),
    principal: normalizarPrincipal(o.principal),
    inscricoes: normalizarInscricoes(o.inscricoes),
    acao_entre_amigos: normalizarAcao(o.acao_entre_amigos),
    loja: normalizarLoja(o.loja),
    despesas: normalizarDespesas(o.despesas),
    entradas_avulsas: normalizarEntradas(o.entradas_avulsas),
    onde_esta: normalizarOndeEsta(o.onde_esta),
  };
}

// --- Datas e textos ---

const DATA_BR = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function dataBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATA_BR.format(d);
}

export function horaBR(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function dataHoraBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${DATA_BR.format(d)} ${horaBR(iso)}`;
}

export function percentual(parte: number, todo: number): number {
  if (todo <= 0) return 0;
  return Math.max(0, Math.min(100, (parte / todo) * 100));
}

export function textoDiferenca(v: number | null, aberto: boolean): string {
  if (aberto) return 'Ainda aberto';
  if (v == null) return '—';
  if (v === 0) return 'Bateu ✓';
  if (v < 0) return `Faltou ${formatarReais(Math.abs(v))}`;
  return `Sobrou ${formatarReais(v)}`;
}

export function corDiferenca(v: number | null, aberto: boolean): string {
  if (aberto || v == null) return 'text-foreground-800';
  if (v === 0) return 'text-success-700';
  if (v < 0) return 'text-danger-700';
  return 'text-warning-700';
}

// Formatador local para evitar dependência circular de importação na lib.
function formatarReais(centavos: number): string {
  const reais = Math.floor(centavos / 100);
  const cents = centavos % 100;
  return `R$ ${reais.toLocaleString('pt-BR')},${String(cents).padStart(2, '0')}`;
}